import {
    MoreOutlined,
    FolderAddOutlined,
} from "@ant-design/icons";
import { Dropdown, Input, Modal } from "antd";
import { useState } from "react";

interface Folder {
    id: string | number;
    name: string;
}

interface Props {
    folders: Folder[];
    activeFolder: string | number | null;
    setActiveFolder: (id: string | number) => void;
    onCreateFolder: (name: string) => void;
    onRenameFolder: (id: string | number, name: string) => void;
    onDeleteFolder: (id: string | number) => void;
}

export default function FolderSidebar({
    folders,
    activeFolder,
    setActiveFolder,
    onCreateFolder,
    onRenameFolder,
    onDeleteFolder,
}: Props) {
    const [showInput, setShowInput] = useState(false);
    const [folderName, setFolderName] = useState("");

    const [editingId, setEditingId] = useState<string | number | null>(null);
    const [editValue, setEditValue] = useState("");

    const [search, setSearch] = useState("");

    // 🔥 normalize tiếng Việt
    const normalize = (str: string) =>
        str
            .toLowerCase()
            .normalize("NFD")
            .replace(/[\u0300-\u036f]/g, "");

    // 🔥 filter folder theo search
    const filteredFolders = folders.filter((f) =>
        normalize(f.name).includes(normalize(search))
    );

    const handleCreate = () => {
        if (!folderName.trim()) return;
        onCreateFolder(folderName);
        setFolderName("");
        setShowInput(false);
    };

    const confirmDelete = (id: string | number) => {
        Modal.confirm({
            title: "Xóa thư mục?",
            content: "Tất cả ảnh trong thư mục sẽ bị ảnh hưởng.",
            okText: "Xóa",
            cancelText: "Hủy",
            okButtonProps: { danger: true },
            centered: true,
            onOk: () => onDeleteFolder(id),
        });
    };

    return (
        <div className="w-full border-r dark:border-gray-700 !pb-0 !pl-0 p-3 overflow-y-auto sm:w-[260px]">
            {/* 🔍 SEARCH + ADD */}
            <div className="flex gap-2 mb-3">
                <input
                    placeholder="Tìm kiếm thư mục..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="flex-1 px-3 py-2 rounded-lg bg-gray-100 dark:bg-gray-800 outline-none"
                />

                <button
                    onClick={() => setShowInput(true)}
                    className="w-9 h-9 flex items-center justify-center bg-[#3e91ab] text-white rounded-lg hover:bg-[#3e91ab]"
                >
                    <FolderAddOutlined />
                </button>
            </div>

            {/* ➕ CREATE FOLDER */}
            {showInput && (
                <div className="mb-3 flex gap-2">
                    <Input
                        autoFocus
                        placeholder="Tên thư mục"
                        value={folderName}
                        onChange={(e) => setFolderName(e.target.value)}
                        onPressEnter={handleCreate}
                    />
                    <button
                        onClick={handleCreate}
                        className="px-3 bg-[#3a859c] text-white rounded-lg hover:opacity-90"
                    >
                        OK
                    </button>
                </div>
            )}

            {/* 📂 LIST */}
            <div className="space-y-2">
                {filteredFolders.length === 0 && (
                    <div className="text-sm text-gray-400 text-center mt-4">
                        Không tìm thấy thư mục
                    </div>
                )}

                {filteredFolders.map((f) => {
                    const menuItems = [
                        {
                            key: "rename",
                            label: "Sửa tên",
                            onClick: () => {
                                setEditingId(f.id);
                                setEditValue(f.name);
                            },
                        },
                        {
                            key: "delete",
                            label: "Xóa thư mục",
                            danger: true,
                            onClick: () => confirmDelete(f.id),
                        },
                    ];

                    return (
                        <div
                            key={f.id}
                            onClick={() => setActiveFolder(f.id)}
                            className={`flex items-center justify-between p-2 rounded-lg border cursor-pointer transition
                                ${activeFolder === f.id
                                    ? "bg-blue-100 dark:bg-blue-900 border-[#3e91ab]"
                                    : "hover:bg-gray-100 dark:hover:bg-gray-800 border-gray-200 dark:border-gray-700"
                                }`}
                        >
                            {/* ✏️ RENAME */}
                            {editingId === f.id ? (
                                <Input
                                    autoFocus
                                    value={editValue}
                                    onChange={(e) => setEditValue(e.target.value)}
                                    onPressEnter={() => {
                                        onRenameFolder(f.id, editValue);
                                        setEditingId(null);
                                    }}
                                    onBlur={() => setEditingId(null)}
                                />
                            ) : (
                                <span className="truncate">{f.name}</span>
                            )}

                            {/* ⋮ MENU */}
                            <Dropdown menu={{ items: menuItems }} trigger={["click"]}>
                                <MoreOutlined
                                    onClick={(e) => e.stopPropagation()}
                                    className="cursor-pointer"
                                />
                            </Dropdown>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}